import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import useInputState from '../../helpers/hooks/useInputState';
import { getOrganizationsRequest, cancelOrganizationsRequest } from '../../store/actions/organizations';
import OrganizationItem from './components/OrganizationItem';
import OrganizationModal from './components/OrganizationModal';
import {
  ORGANIZATION_FILTERS,
  ORGANIZATION_HEADER_COLUMNS,
  ORGANIZATION_MODAL_STATES,
  ORGANIZATION_WIDTHS,
} from './constants';
import MyButton from '../../components/MyButton';
import AddOrgIcon from '../../assets/formIcons/addOrg.svg';
import MyTable from '../../components/MyTable';
import MyCheckbox from '../../components/MyCheckbox';
import './css/index.scss';
import MyLoading from '../../components/MyLoading';

function Organizations() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    organizations,
    organizationsTotal,
    organizationsAllTotal,
    organizationsActiveTotal,
    organizationsBlockedTotal,
    organizationsLoading,
    organizationsError,
  } = useSelector((store) => store.organizations);

  const [page, setPage] = useInputState(1);
  const [perPage, setPerPage] = useState(100);
  const [selectedFilter, setSelectedFilter] = useState(ORGANIZATION_FILTERS[0]);
  const [q, setQ] = useInputState();
  const [isInit, setIsInit] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalState, setModalState] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState({});
  const [itemsPassed, setItemsPassed] = useState(0);
  const [deepSearch, setDeepSearch] = useState(false);
  const abortControllerRef = useRef(null);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);

  const getOrganizations = ({ paramsPage = 1, paramsPerPage = perPage, isInitRequired = true } = {}) => {
    if (isInitRequired && !isInit) return;

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    dispatch(getOrganizationsRequest({
      q,
      page: paramsPage,
      per_page: paramsPerPage,
      is_active: selectedFilter.id,
      should_add_views: 'false',
      sort_by_date: 'true',
      count_all_totals: 'true',
      deep_search: deepSearch ? 'true' : 'false',
      deep_populate: 'true',
    }, abortController));

    setSelectedOrganizations([]);
  };

  const handleSearch = () => {
    setPage(1);
    getOrganizations({ paramsPage: 1 });
  };

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(cancelOrganizationsRequest());
  };

  const TableItemComponent = useCallback((item, index) => (
    <OrganizationItem
      key={item._id}
      item={item}
      itemsPassed={itemsPassed}
      widths={ORGANIZATION_WIDTHS}
      index={index}
      setSelectedOrganization={setSelectedOrganization}
      setModalState={setModalState}
      setIsModalVisible={setIsModalVisible}
      withCheckbox={selectedOrganizations.length > 0}
      isSelected={selectedOrganizations.includes(item._id)}
      setSelected={setSelectedOrganizations}
    />
  ), [itemsPassed, selectedOrganizations]);

  const WhileSelectingComponents = useCallback(() => (
    selectedOrganizations?.length ? (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '20px', marginTop: '10px', marginBottom: '-28px',
      }}
      >
        Выбрано:
        {' '}
        {selectedOrganizations.length}
        <div
          className="pressable"
          onClick={() => setSelectedOrganizations([])}
        >
          Снять выбор со всех
        </div>
        <div
          onClick={() => {
            setModalState(ORGANIZATION_MODAL_STATES.block);
            setIsModalVisible(true);
          }}
          className="pressable"
        >
          Забанить
        </div>
        <div
          onClick={() => {
            setModalState(ORGANIZATION_MODAL_STATES.changeOwner);
            setIsModalVisible(true);
          }}
          className="pressable"
        >
          Изменить владельца
        </div>
        <div
          onClick={() => {
            setModalState(ORGANIZATION_MODAL_STATES.fullyDelete);
            setIsModalVisible(true);
          }}
          className="pressable"
        >
          Удалить полностью
        </div>
      </div>
    ) : null
  ), [selectedOrganizations]);

  const handlePage = (newVal) => {
    if (newVal === page) return;
    setPage(newVal);
    getOrganizations({ paramsPage: newVal });
  };

  const onCreateOrganizationClick = () => {
    navigate('/organizations/create');
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);

      if (perPage < 1) {
        setPerPage(1);
        getOrganizations({ paramsPage: 1, paramsPerPage: 1 });
      } else if (perPage > 500) {
        setPerPage(500);
        getOrganizations({ paramsPage: 1, paramsPerPage: 500 });
      } else {
        getOrganizations({ paramsPage: 1 });
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [perPage]);

  useEffect(() => {
    setPage(1);
    getOrganizations({ paramsPage: 1 });
  }, [selectedFilter]);

  useEffect(() => {
    getOrganizations({ paramsPage: page, isInitRequired: false });
    setTimeout(() => {
      setIsInit(true);
    }, 500);
  }, []);

  useEffect(() => {
    setItemsPassed((page - 1) * perPage);
  }, [organizations]);

  return (
    <div>
      <PageTitle
        text="Организации (во всех странах)"
        RightComponent={(
          <div className="organizations_table">
            <MyButton
              text="Добавить"
              svgPath={AddOrgIcon}
              className="org_add_btn"
              onClick={onCreateOrganizationClick}
            />
            <div className="organizations_table_loading">
              {organizationsLoading && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <p>Загрузка...</p>
                <MyLoading strokeWidth={4} width={30} height={30} />
              </div>
              )}
            </div>
            <div className="organizations_table_checkbox">
              <MyCheckbox
                text="Глубокий поиск"
                setValue={setDeepSearch}
                value={deepSearch}
              />
            </div>
          </div>
        )}
      />
      <MyTable
        q={q}
        setQ={setQ}
        filters={ORGANIZATION_FILTERS}
        filtersCount={{
          [ORGANIZATION_FILTERS[0].id]: organizationsAllTotal || 0,
          [ORGANIZATION_FILTERS[1].id]: organizationsActiveTotal || 0,
          [ORGANIZATION_FILTERS[2].id]: organizationsBlockedTotal || 0,
        }}
        perPage={perPage}
        setPerPage={setPerPage}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        // headerColumns={ORGANIZATION_HEADER_COLUMNS}
        Item={TableItemComponent}
        WhileSelectingComponents={WhileSelectingComponents}
        data={organizations}
        page={page}
        setPage={handlePage}
        total={organizationsTotal}
        onSearch={handleSearch}
        isLoading={organizationsLoading}
        onCancelSearch={handleCancelSearch}
      />

      <OrganizationModal
        selectedOrganization={selectedOrganization}
        modalState={modalState}
        isVisible={isModalVisible}
        setIsVisible={setIsModalVisible}
        setSelectedOrganization={setSelectedOrganization}
        selectedOrganizations={selectedOrganizations}
        setSelectedOrganizations={setSelectedOrganizations}
        getOrganizations={getOrganizations}
      />
    </div>
  );
}

export default Organizations;
